/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { generateUuid } from '../../../base/common/uuid.js';
import type {
	IAgentDeltaEvent,
	IAgentErrorEvent,
	IAgentProgressEvent,
	IAgentReasoningEvent,
	IAgentTitleChangedEvent,
	IAgentToolCompleteEvent,
	IAgentToolStartEvent,
	IAgentUsageEvent
} from '../common/agentService.js';
import {
	ActionType,
	type ISessionAction,
	type ISessionErrorAction,
	type ITitleChangedAction,
	type IToolCallCompleteAction,
	type IToolCallReadyAction,
	type IToolCallStartAction,
	type ITurnCompleteAction,
	type IUsageAction
} from '../common/state/sessionActions.js';
import { ResponsePartKind, ToolCallConfirmationReason, type URI } from '../common/state/sessionState.js';

/**
 * Stateful mapper that tracks the "current" markdown and reasoning response
 * parts per session/turn so that streaming deltas can be routed to the correct
 * part via `partId`.
 *
 * Call {@link reset} when a new turn starts to clear tracked part IDs.
 */
export class AgentEventMapper {
	/** Current markdown part ID per session. Reset on each new turn. */
	private readonly _currentMarkdownPartId = new Map<string, string>();
	/** Current reasoning part ID per session. Reset on each new turn. */
	private readonly _currentReasoningPartId = new Map<string, string>();

	/**
	 * Resets tracked part IDs for a session (call when a new turn starts).
	 */
	reset(session: string): void {
		this._currentMarkdownPartId.delete(session);
		this._currentReasoningPartId.delete(session);
	}

	/**
	 * Maps a flat {@link IAgentProgressEvent} from the agent host into
	 * protocol {@link ISessionAction}(s) suitable for dispatch to the reducer.
	 *
	 * Returns `undefined` for events that have no corresponding action.
	 * May return an array when a single SDK event maps to multiple protocol actions.
	 */
	mapProgressEventToActions(event: IAgentProgressEvent, session: URI, turnId: string): ISessionAction | ISessionAction[] | undefined {
		switch (event.type) {
			case 'delta': {
				const e = event as IAgentDeltaEvent;
				const existingPartId = this._currentMarkdownPartId.get(session);
				if (!existingPartId) {
					// Create a new markdown part with the content directly
					const partId = generateUuid();
					this._currentMarkdownPartId.set(session, partId);
					return {
						type: ActionType.SessionResponsePart,
						session,
						turnId,
						part: { kind: ResponsePartKind.Markdown, id: partId, content: e.content },
					};
				}
				return {
					type: ActionType.SessionDelta,
					session,
					turnId,
					partId: existingPartId,
					content: e.content,
				};
			}

			case 'tool_start': {
				// A new tool call invalidates the current markdown part so the
				// next text delta creates a fresh part after the tool call.
				this._currentMarkdownPartId.delete(session);

				// The Copilot SDK provides full parameters at tool_start time.
				// We emit both toolCallStart (streaming → created) and toolCallReady
				// (params complete → running with auto-confirm) as a pair.
				const e = event as IAgentToolStartEvent;
				const startAction: IToolCallStartAction = {
					type: ActionType.SessionToolCallStart,
					session,
					turnId,
					toolCallId: e.toolCallId,
					toolName: e.toolName,
					displayName: e.displayName,
					_meta: { toolKind: e.toolKind, language: e.language },
				};
				const readyAction: IToolCallReadyAction = {
					type: ActionType.SessionToolCallReady,
					session,
					turnId,
					toolCallId: e.toolCallId,
					invocationMessage: e.invocationMessage,
					toolInput: e.toolInput,
					confirmed: ToolCallConfirmationReason.NotNeeded,
				};
				return [startAction, readyAction];
			}

			case 'tool_ready': {
				// A running tool requires re-confirmation (e.g. mid-execution permission).
				// Emit toolCallReady WITHOUT confirmed, which transitions
				// Running → PendingConfirmation in the reducer.
				const e = event;
				return {
					type: ActionType.SessionToolCallReady,
					session,
					turnId,
					toolCallId: e.toolCallId,
					invocationMessage: e.invocationMessage,
					toolInput: e.toolInput,
					confirmationTitle: e.confirmationTitle,
				} satisfies IToolCallReadyAction;
			}

			case 'tool_complete': {
				const e = event as IAgentToolCompleteEvent;
				return {
					type: ActionType.SessionToolCallComplete,
					session,
					turnId,
					toolCallId: e.toolCallId,
					result: e.result,
				} satisfies IToolCallCompleteAction;
			}

			case 'idle':
				return {
					type: ActionType.SessionTurnComplete,
					session,
					turnId,
				} satisfies ITurnCompleteAction;

			case 'error': {
				const e = event as IAgentErrorEvent;
				return {
					type: ActionType.SessionError,
					session,
					turnId,
					error: {
						errorType: e.errorType,
						message: e.message,
						stack: e.stack,
					},
				} satisfies ISessionErrorAction;
			}

			case 'usage': {
				const e = event as IAgentUsageEvent;
				return {
					type: ActionType.SessionUsage,
					session,
					turnId,
					usage: {
						inputTokens: e.inputTokens,
						outputTokens: e.outputTokens,
						model: e.model,
						cacheReadTokens: e.cacheReadTokens,
					},
				} satisfies IUsageAction;
			}

			case 'title_changed':
				return {
					type: ActionType.SessionTitleChanged,
					session,
					title: (event as IAgentTitleChangedEvent).title,
				} satisfies ITitleChangedAction;

			case 'reasoning': {
				const e = event as IAgentReasoningEvent;
				const existingPartId = this._currentReasoningPartId.get(session);
				if (!existingPartId) {
					// Create a new reasoning part with the content directly
					const partId = generateUuid();
					this._currentReasoningPartId.set(session, partId);
					return {
						type: ActionType.SessionResponsePart,
						session,
						turnId,
						part: { kind: ResponsePartKind.Reasoning, id: partId, content: e.content },
					};
				}
				return {
					type: ActionType.SessionReasoning,
					session,
					turnId,
					partId: existingPartId,
					content: e.content,
				};
			}

			case 'message':
				return undefined;

			default:
				return undefined;
		}
	}
}
